"use client";

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

interface FlipCounterProps {
  value: number;
  className?: string;
  currencySymbol?: string;
}

const getFormattedValue = (num: number, currencySymbol: string): string => {
  const formatted = new Intl.NumberFormat('es-AR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);

  return currencySymbol ? `${currencySymbol}${formatted}` : formatted;
};

const FlipCounter: React.FC<FlipCounterProps> = ({ value, className, currencySymbol = '$' }) => {
  const [displayValue, setDisplayValue] = useState(value);
  const [isFlipping, setIsFlipping] = useState(false);
  const formattedValue = getFormattedValue(displayValue, currencySymbol);

  // Detect when value changes and trigger flip animation
  useEffect(() => {
    if (value !== displayValue) {
      setIsFlipping(true);
      // Trigger flip animation, then update value midway through
      const timer = setTimeout(() => {
        setDisplayValue(value);
        setIsFlipping(false);
      }, 300); // Match animation duration
      return () => clearTimeout(timer);
    }
  }, [value, displayValue]);

  return (
    <motion.span
      className={cn("inline-flex items-center whitespace-nowrap", className)}
      style={{ fontFamily: "'Titillium Web', system-ui, sans-serif", fontWeight: 500 }}
      animate={isFlipping ? { rotateX: 90, opacity: 0.5 } : { rotateX: 0, opacity: 1 }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
    >
      {formattedValue}
    </motion.span>
  );
};

export default FlipCounter;